// NOTICE: KAPTION-INTEGRATION
// Per-user chat preferences (delivery, Enter to send, notes default, live
// translation, language), shared by every place that shows
// the Kaption preferences panel: the conversation list header and the reply
// box. All values persist to ui_settings, exactly as the legacy topbar
// settings sub-panel does, so both surfaces read and write the same keys.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { trackUsage } from 'dashboard/composables/useUsageAnalytics';
import { USAGE_ANALYTICS_EVENTS } from 'dashboard/helper/usageAnalytics/constants';
import {
  KAPTION_DELIVERY_MODES,
  resolveKaptionDeliveryMode,
} from 'dashboard/helper/kaptionDeliveryMode';
import {
  KAPTION_MERGE_LEVELS,
  resolveKaptionMergeLevel,
} from 'dashboard/helper/kaptionMessageMerge';

export const UNDO_DURATION_OPTIONS = [3, 5, 10, 15];

// Same set the legacy topbar language selector offers.
const SUPPORTED_LANGUAGE_CODES = [
  'en',
  'es',
  'pt',
  'fr',
  'it',
  'de',
  'tr',
  'zh',
];

export function useKaptionChatPreferences() {
  const { t, locale } = useI18n();
  const { uiSettings, updateUISettings } = useUISettings();
  const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
  const { canUseFeature } = useFeaturePermissions();
  const { enabledLanguages } = useConfig();
  const { currentAccount } = useAccount();

  const showSendSettings = computed(() =>
    isKaptionFlagEnabled('KAPTION_SHOW_SEND_SETTINGS')
  );
  // whatsapp-message-merging: while KAPTION_MESSAGE_MERGING is on, the old
  // quantity Combine modes are never offered.
  const messageMergingEnabled = computed(() =>
    isKaptionFlagEnabled('KAPTION_MESSAGE_MERGING')
  );
  const messageBatchingEnabled = computed(
    () =>
      isKaptionFlagEnabled('KAPTION_MESSAGE_BATCHING') &&
      !messageMergingEnabled.value
  );
  const showLiveTranslationSetting = computed(() =>
    isKaptionFlagEnabled('KAPTION_LIVE_TRANSLATION')
  );
  const showWriteNotesSetting = computed(() => canUseFeature('private_notes'));

  const deliveryMode = computed({
    get: () =>
      resolveKaptionDeliveryMode(uiSettings.value, {
        batchingEnabled: messageBatchingEnabled.value,
      }),
    set: value =>
      updateUISettings({
        kaption_delivery_mode: value,
        kaption_undo_send_enabled: value === KAPTION_DELIVERY_MODES.UNDO,
      }),
  });

  const deliveryModes = computed(() =>
    messageBatchingEnabled.value
      ? [
          KAPTION_DELIVERY_MODES.IMMEDIATE,
          KAPTION_DELIVERY_MODES.UNDO,
          KAPTION_DELIVERY_MODES.PAIR,
          KAPTION_DELIVERY_MODES.TRIO,
          KAPTION_DELIVERY_MODES.BUNDLE,
        ]
      : [KAPTION_DELIVERY_MODES.IMMEDIATE, KAPTION_DELIVERY_MODES.UNDO]
  );

  const deliveryModeLabel = mode =>
    t(`KAPTION_PLATFORM.SETTINGS.DELIVERY.MODES.${mode}`);

  // whatsapp-message-merging: Inmediato / Equilibrado / Agrupar, per agent,
  // with an account default and an optional admin lock.
  const accountSettings = computed(() => currentAccount.value?.settings || {});
  const mergeLevels = Object.values(KAPTION_MERGE_LEVELS);
  const mergeLocked = computed(
    () => !!accountSettings.value.kaption_merge_locked
  );
  const accountMergeDefault = computed(() =>
    resolveKaptionMergeLevel({
      accountSettings: { ...accountSettings.value, kaption_merge_locked: true },
    })
  );
  const mergeLevel = computed({
    get: () =>
      resolveKaptionMergeLevel({
        uiSettings: uiSettings.value,
        accountSettings: accountSettings.value,
      }),
    set: value => {
      if (mergeLocked.value) return;
      updateUISettings({ kaption_merge_level: value });
    },
  });
  const mergeLevelLabel = level =>
    t(`KAPTION_MESSAGE_MERGE.SETTINGS.LEVELS.${level}`);
  // Undo stays its own toggle; it writes the same keys the Delivery picker does.
  const undoEnabled = computed({
    get: () => deliveryMode.value === KAPTION_DELIVERY_MODES.UNDO,
    set: value => {
      deliveryMode.value = value
        ? KAPTION_DELIVERY_MODES.UNDO
        : KAPTION_DELIVERY_MODES.IMMEDIATE;
    },
  });

  const undoDuration = computed({
    get: () => uiSettings.value?.kaption_undo_send_duration ?? 15,
    set: value => updateUISettings({ kaption_undo_send_duration: value }),
  });

  const enterToSend = computed({
    get: () => uiSettings.value?.enter_to_send_enabled ?? true,
    set: value => updateUISettings({ enter_to_send_enabled: value }),
  });

  const liveTranslation = computed({
    get: () => uiSettings.value?.kaption_live_translation_enabled ?? true,
    set: value => {
      updateUISettings({ kaption_live_translation_enabled: value });
      // usage-analytics.TRANSLATION.1 (master toggle)
      trackUsage(USAGE_ANALYTICS_EVENTS.LIVE_TRANSLATION_TOGGLED, {
        scope: 'master',
        enabled: Boolean(value),
      });
    },
  });

  const writeNotesDefault = computed({
    get: () => uiSettings.value?.kaption_write_notes_default ?? false,
    set: value => updateUISettings({ kaption_write_notes_default: value }),
  });

  const accountDefaultLocale = computed(
    () => currentAccount.value?.locale || 'en'
  );

  const languages = computed(() => {
    const defaultOption = {
      code: '',
      name: t('KAPTION_PLATFORM.SETTINGS.LANGUAGE_DEFAULT'),
      isDefault: true,
    };
    const options = (enabledLanguages || [])
      .filter(lang => SUPPORTED_LANGUAGE_CODES.includes(lang.iso_639_1_code))
      .map(lang => ({
        code: lang.iso_639_1_code,
        name: lang.name,
        isDefault: false,
      }));
    return [defaultOption, ...options];
  });

  // Empty string means "use the account default".
  const selectedLanguage = computed({
    get: () => uiSettings.value?.locale ?? '',
    set: async value => {
      if (value === '') {
        await updateUISettings({ locale: null });
        locale.value = accountDefaultLocale.value;
        return;
      }
      const valid = (enabledLanguages || []).some(
        l => l.iso_639_1_code === value
      );
      if (valid) {
        await updateUISettings({ locale: value });
        locale.value = value;
      }
    },
  });

  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  const enterToSendHint = computed(() => {
    const key = isMac ? '⌘+Enter' : 'Ctrl+Enter';
    return enterToSend.value
      ? t('KAPTION_PLATFORM.SETTINGS.ENTER_TO_SEND.NEW_LINE', { key })
      : t('KAPTION_PLATFORM.SETTINGS.ENTER_TO_SEND.TO_SEND', { key });
  });

  return {
    KAPTION_DELIVERY_MODES,
    KAPTION_MERGE_LEVELS,
    messageMergingEnabled,
    mergeLevels,
    mergeLevel,
    mergeLevelLabel,
    mergeLocked,
    accountMergeDefault,
    undoEnabled,
    showSendSettings,
    showLiveTranslationSetting,
    showWriteNotesSetting,
    deliveryMode,
    deliveryModes,
    deliveryModeLabel,
    undoDuration,
    enterToSend,
    enterToSendHint,
    liveTranslation,
    writeNotesDefault,
    languages,
    selectedLanguage,
    accountDefaultLocale,
  };
}
